import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTab } from './TabContext';
import { fetchAgents, streamInvestigation } from '../services/investigatorApi';
import {
  ConversationNotFoundError,
  deleteConversation,
  fetchConversationMessages,
  listConversations,
} from '../services/zoeConversationsApi';
import type { AgentSummary, InvestigationProgressEvent } from '../types/investigator';
import type { ConversationSummary } from '../types/zoeConversations';
import type { ProfitAgentDrilldown } from '../types/profitabilityAgent';
import { buildStoredProfitDrilldown, writeStoredProfitDrilldown } from '../utils/profitDrilldownNavigation';
import { formatZoeInvestigationError, isLikelyProfitQuestion } from '../utils/zoeInvestigationErrors';
import { isZoeChipIntroOnboarded, markZoeChipIntroOnboarded } from '../components/investigator/QueryBar';
import {
  applyThinkingProgress,
  createInitialThinkingModel,
} from '../components/zoeAssistant/zoeThinkingModel';
import { turnFromMessage, type ZoeTurn } from '../components/zoeAssistant/zoeTurn';

/** T29 — the option picked on a clarification card: binds the turn to it. */
export interface ClarificationPick {
  clarification_id: string;
  option_id: string;
}

interface ZoeAskOptions {
  /** Set when this turn answers a clarification by clicking an option. */
  clarificationResponse?: ClarificationPick;
  /** Ask in a new conversation rather than the one open (which stays in
   *  History) — for a question handed over from another page, such as
   *  Explain's "Continue in ZOE", which must not inherit an unrelated
   *  conversation's context (e.g. a period picked earlier). */
  newConversation?: boolean;
}

/**
 * What changes only when a question starts or finishes. Split from the thread
 * so components that just open the pop-up or ask (the launcher, Orders rows)
 * don't re-render on every streaming progress event.
 */
interface ZoeControlsValue {
  isLoading: boolean;
  ask: (question: string, options?: ZoeAskOptions) => Promise<void>;
  retry: () => void;
  /** Drop the thread and its server-side id. The conversation itself stays in History. */
  newConversation: () => void;
  openProfitabilityReport: (d: ProfitAgentDrilldown) => void;
  /** The global pop-up (ZoeLauncher). Lives here so other pages can open it. */
  popupOpen: boolean;
  setPopupOpen: (open: boolean) => void;
  /** Whether this user gets the pop-up at all (App's access check). Hand-offs
   *  such as Explain's "Continue in ZOE" only render when it is true. */
  popupAvailable: boolean;
}

/** The thread itself — changes on every progress event while ZOE works. */
interface ZoeThreadValue {
  turns: ZoeTurn[];
  /** GET /agents, id -> display name, for the per-turn agent badge. */
  agentLabels: Record<string, string>;
  entitySuggestions: string[];
  chipIntroOnboarded: boolean;
  activeProfitDrilldown: ProfitAgentDrilldown | null;
  setActiveProfitDrilldown: (d: ProfitAgentDrilldown | null) => void;
  markTurnRevealed: (turnId: string) => void;
  /** Surfaces call this on mount; the registry is only fetched once someone shows ZOE. */
  ensureAgentLabels: () => void;
  /** T30 — the open conversation's server id, once its first answer has arrived. */
  conversationId: string | undefined;
  /** T30 — the open conversation's name (its first question). */
  conversationTitle: string;
  /** T30 — the conversation has older messages than the ones loaded. */
  hasEarlier: boolean;
  loadingEarlier: boolean;
  loadEarlierMessages: () => Promise<void>;
  /** T30 — the user's recent conversations, for the full page's History. */
  recentConversations: ConversationSummary[];
  refreshRecent: () => Promise<void>;
  /** T30 — the conversation being opened, while it loads. */
  resumingId: string | null;
  resumeError: string | null;
  /** T30 — reopen a saved conversation. Resolves true once it is showing. */
  resumeConversation: (id: string, options?: { quietIfMissing?: boolean }) => Promise<boolean>;
  /** T30 — delete a saved conversation for good; closes it if it is the one open. */
  removeConversation: (id: string) => Promise<void>;
  /** T30 — surfaces call this when they first show: reopens the conversation
   *  this tab was in before a reload, once, unless something has started. */
  ensureResumed: () => void;
}

type ZoeConversationValue = ZoeControlsValue & ZoeThreadValue;

const ZoeControlsContext = createContext<ZoeControlsValue | undefined>(undefined);
const ZoeThreadContext = createContext<ZoeThreadValue | undefined>(undefined);

const LS_ENTITY_SUGGESTIONS_KEY = 'zoe_ops_assistant_entity_suggestions_v1';

/**
 * T30 — the open conversation, remembered for this browser tab only, so an
 * accidental reload lands back in it. Session-scoped on purpose: a new visit
 * starts fresh, with past conversations one click away in the Recent list —
 * the same behaviour as mainstream chat products. Not in the URL, because
 * App's router reads everything after `#tab=` as the tab id.
 */
const ACTIVE_CONVERSATION_KEY = 'zoe.activeConversationId';
/** Messages loaded when a conversation opens, and per "show earlier". */
const RESUME_PAGE_SIZE = 20;
/** A question another page has handed ZOE to ask (the ZOE page reads and clears it). */
const QUICK_QUESTION_KEY = 'agents_quick_action_ops_assistant_question';

function rememberActiveConversation(id: string | undefined): void {
  try {
    if (id) sessionStorage.setItem(ACTIVE_CONVERSATION_KEY, id);
    else sessionStorage.removeItem(ACTIVE_CONVERSATION_KEY);
  } catch {
    // Storage unavailable (private mode, quota): reload continuity is a
    // convenience, and everything else works without it.
  }
}

function rememberedActiveConversation(): string | null {
  try {
    return sessionStorage.getItem(ACTIVE_CONVERSATION_KEY);
  } catch {
    return null;
  }
}

function mergeEntitySuggestions(prev: string[], next: string[]): string[] {
  const cleaned = [...prev, ...next].map((s) => String(s || '').trim()).filter(Boolean);
  const out: string[] = [];
  for (let i = cleaned.length - 1; i >= 0; i--) {
    const v = cleaned[i];
    if (!out.includes(v)) out.push(v);
  }
  return out.slice(0, 50);
}

function readStoredEntitySuggestions(): string[] {
  try {
    const raw = localStorage.getItem(LS_ENTITY_SUGGESTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.map((s) => String(s || '')).filter(Boolean) : [];
  } catch {
    return [];
  }
}

/**
 * The ZOE conversation, shared by the full ZOE page (Agents tab) and the
 * global pop-up, so "Open full ZOE" continues the same thread and an answer
 * keeps streaming when either surface is hidden. It is also the one place
 * that asks (T29 clarification picks included) and that opens, pages back
 * through and deletes saved conversations (T30), so both surfaces behave
 * alike. Must sit inside TabProvider.
 */
export function ZoeConversationProvider({
  children,
  popupAvailable = false,
}: {
  children: ReactNode;
  popupAvailable?: boolean;
}) {
  const { setActiveTab } = useTab();

  const [turns, setTurns] = useState<ZoeTurn[]>([]);
  // Server-assigned on the first turn's response; sent back on every
  // follow-up so the backend appends to the same ConversationStore doc.
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [conversationTitle, setConversationTitle] = useState('');
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [resumingId, setResumingId] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [recentConversations, setRecentConversations] = useState<ConversationSummary[]>([]);
  const [agentLabels, setAgentLabels] = useState<Record<string, string>>({});
  const [entitySuggestions, setEntitySuggestions] = useState<string[]>(readStoredEntitySuggestions);
  const [chipIntroOnboarded, setChipIntroOnboarded] = useState(() => isZoeChipIntroOnboarded());
  const [activeProfitDrilldown, setActiveProfitDrilldown] = useState<ProfitAgentDrilldown | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const lastQuestionRef = useRef('');
  // The clarification pick the last question answered, so a retry stays bound to it.
  const lastClarificationRef = useRef<ClarificationPick | undefined>(undefined);
  const isSubmittingRef = useRef(false);
  // Bumped whenever the thread is replaced (new conversation, another one
  // opened), so a page of earlier messages that arrives afterwards is dropped
  // rather than prepended to a thread it doesn't belong to.
  const threadGenerationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const agentLabelsRequestedRef = useRef(false);
  const resumeAttemptedRef = useRef(false);
  // Read by ensureResumed from a surface's mount effect, which runs after
  // this layout effect has caught the ref up with the render.
  const turnsRef = useRef(turns);
  useLayoutEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  const latestTurn = turns[turns.length - 1];
  const isLoading = latestTurn?.status === 'loading';

  // Abort an in-flight investigation if the app shell unmounts (logout).
  useEffect(() => () => abortRef.current?.abort(), []);

  const ensureAgentLabels = useCallback(() => {
    if (agentLabelsRequestedRef.current) return;
    agentLabelsRequestedRef.current = true;
    fetchAgents()
      .then((agents: AgentSummary[]) => {
        setAgentLabels(Object.fromEntries(agents.map((a) => [a.id, a.name])));
      })
      .catch(() => {
        // Best-effort — badges fall back to a humanised agent_id meanwhile;
        // the next surface to mount tries again.
        agentLabelsRequestedRef.current = false;
      });
  }, []);

  const handleProgress = useCallback((turnId: string, event: InvestigationProgressEvent) => {
    setTurns((prev) =>
      prev.map((t) =>
        t.id === turnId
          ? { ...t, thinkingModel: applyThinkingProgress(t.thinkingModel, event) }
          : t,
      ),
    );
  }, []);

  const openProfitabilityReport = useCallback(
    (d: ProfitAgentDrilldown) => {
      const ctx = latestTurn?.result?.profit_delegation?.context;
      const date_from = ctx?.date_from;
      const date_to = ctx?.date_to;
      if (!date_from || !date_to) return;
      const stored = buildStoredProfitDrilldown(
        d,
        { date_from, date_to },
        {
          question: latestTurn?.result?.question,
          tools_used: latestTurn?.result?.profit_delegation?.tools_used,
        },
      );
      writeStoredProfitDrilldown(stored);
      setActiveTab('profitability-report');
    },
    [latestTurn?.result?.profit_delegation, latestTurn?.result?.question, setActiveTab],
  );

  /** T30 — the user's recent conversations. Secondary: everything works without it. */
  const refreshRecent = useCallback(async () => {
    try {
      setRecentConversations(await listConversations(20));
    } catch {
      // Leave whatever list we had; nothing else depends on it.
    }
  }, []);

  const ask = useCallback(async (question: string, options?: ZoeAskOptions) => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || isSubmittingRef.current) return;

    const fresh = Boolean(options?.newConversation);
    // The id this question is sent under. Read here, not after the resets
    // below: state set in this call isn't visible until the next render.
    const askConversationId = fresh ? undefined : conversationId;

    if (!fresh && /open.*profitability report/i.test(trimmedQuestion) && latestTurn?.result?.profit_delegation?.context) {
      openProfitabilityReport({ type: 'vehicle', id: '', label: 'Full report' });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const clarificationResponse = options?.clarificationResponse;
    const turnId = `turn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    isSubmittingRef.current = true;
    lastQuestionRef.current = trimmedQuestion;
    lastClarificationRef.current = clarificationResponse;
    if (fresh) {
      threadGenerationRef.current += 1;
      setConversationId(undefined);
      setConversationTitle('');
      setHasEarlier(false);
      setResumeError(null);
      rememberActiveConversation(undefined);
    }
    setTurns((prev) => [
      ...(fresh ? [] : prev),
      {
        id: turnId,
        question: trimmedQuestion,
        status: 'loading',
        thinkingModel: createInitialThinkingModel(),
        thinkingStartedAt: Date.now(),
        ...(clarificationResponse ? { answeredClarification: clarificationResponse } : {}),
      },
    ]);
    setActiveProfitDrilldown(null);
    try {
      // NOTE: the Profit Agent period (sessionStorage) used to be injected here
      // as `context.time_window`, which silently clamped every Zoe query to that
      // range and made this tab disagree with Investigator for the same order.
      // Disabled until Zoe has a strong working version — keep the two surfaces
      // sending the same payload. Restore via readStoredProfitAgentPeriod() when
      // window inheritance is deliberate and understood.
      const data = await streamInvestigation(
        {
          question: trimmedQuestion,
          conversationId: askConversationId,
          ...(clarificationResponse ? { clarificationResponse } : {}),
        },
        (event) => handleProgress(turnId, event),
        controller.signal,
      );
      setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, status: 'success', result: data } : t)));
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
        rememberActiveConversation(data.conversation_id);
        if (!askConversationId) {
          // A new conversation is named by its first question; list it now so
          // it is there to come back to. Existing ones are not re-listed per
          // turn — the list is a scan of the user's conversations, cheap when
          // opened, wasteful on every answer.
          setConversationTitle(trimmedQuestion);
          void refreshRecent();
        }
      }
      markZoeChipIntroOnboarded();
      setChipIntroOnboarded(true);

      try {
        const fromEntities = (data.entities || []).map((e) => e.label || e.id);
        const fromEvidenceRefs = (data.evidence || []).flatMap((e) => e.canonical_entity_refs || []);
        setEntitySuggestions((prev) => {
          const merged = mergeEntitySuggestions(prev, [...fromEntities, ...fromEvidenceRefs]);
          try {
            localStorage.setItem(LS_ENTITY_SUGGESTIONS_KEY, JSON.stringify(merged));
          } catch {
            // ignore
          }
          return merged;
        });
      } catch {
        // ignore
      }
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      const message = formatZoeInvestigationError(err, { isProfitQuestion: isLikelyProfitQuestion(trimmedQuestion) });
      setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, status: 'error', error: message } : t)));
    } finally {
      // Only the newest request owns the flag: an aborted one settling late
      // must not clear it while a newer question is still being answered.
      if (abortRef.current === controller) isSubmittingRef.current = false;
    }
  }, [conversationId, handleProgress, latestTurn?.result?.profit_delegation, openProfitabilityReport, refreshRecent]);

  const retry = useCallback(() => {
    if (!lastQuestionRef.current || isSubmittingRef.current) return;
    const clarificationResponse = lastClarificationRef.current;
    void ask(lastQuestionRef.current, clarificationResponse ? { clarificationResponse } : undefined);
  }, [ask]);

  const newConversation = useCallback(() => {
    abortRef.current?.abort();
    isSubmittingRef.current = false;
    threadGenerationRef.current += 1;
    setTurns([]);
    setConversationId(undefined);
    setConversationTitle('');
    setHasEarlier(false);
    setResumeError(null);
    setActiveProfitDrilldown(null);
    rememberActiveConversation(undefined);
  }, []);

  /**
   * T30 — reopen a conversation where it was left: its latest messages, each
   * answer exactly as it was first shown. `quietIfMissing` is for reload
   * continuity, where a vanished conversation should just mean a fresh start.
   * `onlyIfIdle` is too: it gives way to a question asked meanwhile, instead
   * of cancelling it.
   */
  const openConversation = useCallback(
    async (
      id: string,
      { quietIfMissing = false, onlyIfIdle = false }: { quietIfMissing?: boolean; onlyIfIdle?: boolean } = {},
    ): Promise<boolean> => {
      const busy = () => isSubmittingRef.current || turnsRef.current.length > 0;
      if (onlyIfIdle) {
        if (busy()) return false;
      } else {
        abortRef.current?.abort();
        isSubmittingRef.current = false;
      }
      setResumingId(id);
      setResumeError(null);
      try {
        const page = await fetchConversationMessages(id, { limit: RESUME_PAGE_SIZE });
        if (onlyIfIdle && busy()) return false;
        threadGenerationRef.current += 1;
        setTurns(page.messages.map(turnFromMessage));
        setConversationId(page.conversation_id);
        setConversationTitle(page.title);
        setHasEarlier(page.has_more);
        setActiveProfitDrilldown(null);
        rememberActiveConversation(page.conversation_id);
        return true;
      } catch (err) {
        if (err instanceof ConversationNotFoundError) {
          rememberActiveConversation(undefined);
          if (!quietIfMissing) setResumeError('That conversation no longer exists.');
          void refreshRecent();
        } else {
          setResumeError(err instanceof Error ? err.message : 'Could not open that conversation.');
        }
        return false;
      } finally {
        setResumingId(null);
      }
    },
    [refreshRecent],
  );

  const resumeConversation = useCallback(
    (id: string, options?: { quietIfMissing?: boolean }) => openConversation(id, options),
    [openConversation],
  );

  /**
   * T30 — once per session in this tab, when ZOE first shows (page or pop-up):
   * reopen the conversation this tab was in before a reload. Skipped when
   * another page has handed ZOE a question to ask, or one is already under
   * way (e.g. "Continue in ZOE"): that question goes to a fresh conversation,
   * and resuming at the same time would race it into the old one.
   */
  const ensureResumed = useCallback(() => {
    if (resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;
    let pendingQuickQuestion = false;
    try {
      pendingQuickQuestion = Boolean(localStorage.getItem(QUICK_QUESTION_KEY));
    } catch {
      // ignore
    }
    const remembered = rememberedActiveConversation();
    if (!remembered || pendingQuickQuestion) return;
    void openConversation(remembered, { quietIfMissing: true, onlyIfIdle: true });
  }, [openConversation]);

  /**
   * T30 — page back through a long conversation. The thread scrolls inside
   * its surface's own container rather than the window, so instead of
   * adjusting a scroll offset, the message the reader was at is brought back
   * into view once older ones have been added above it.
   */
  const loadEarlierMessages = useCallback(async () => {
    const first = turns[0];
    if (!conversationId || !first?.turnId || loadingEarlier) return;
    const generation = threadGenerationRef.current;
    setLoadingEarlier(true);
    try {
      const page = await fetchConversationMessages(conversationId, { before: first.turnId, limit: RESUME_PAGE_SIZE });
      // The thread was replaced while this page loaded: it belongs to another conversation.
      if (generation !== threadGenerationRef.current) return;
      setTurns((prev) => [...page.messages.map(turnFromMessage), ...prev]);
      setHasEarlier(page.has_more);
      requestAnimationFrame(() => {
        document.querySelector(`[data-turn-id="${CSS.escape(first.id)}"]`)?.scrollIntoView({ block: 'start' });
      });
    } catch {
      // `hasEarlier` stays set, so the control stays and the user can retry.
    } finally {
      setLoadingEarlier(false);
    }
  }, [conversationId, loadingEarlier, turns]);

  const removeConversation = useCallback(
    async (id: string) => {
      await deleteConversation(id);
      setRecentConversations((prev) => prev.filter((c) => c.conversation_id !== id));
      if (id === conversationId) newConversation();
    },
    [conversationId, newConversation],
  );

  const markTurnRevealed = useCallback((turnId: string) => {
    setTurns((prev) =>
      prev.some((t) => t.id === turnId && !t.revealed)
        ? prev.map((t) => (t.id === turnId ? { ...t, revealed: true } : t))
        : prev,
    );
  }, []);

  const controls = useMemo<ZoeControlsValue>(
    () => ({
      isLoading,
      ask,
      retry,
      newConversation,
      openProfitabilityReport,
      popupOpen,
      setPopupOpen,
      popupAvailable,
    }),
    [isLoading, ask, retry, newConversation, openProfitabilityReport, popupOpen, popupAvailable],
  );

  const thread = useMemo<ZoeThreadValue>(
    () => ({
      turns,
      agentLabels,
      entitySuggestions,
      chipIntroOnboarded,
      activeProfitDrilldown,
      setActiveProfitDrilldown,
      markTurnRevealed,
      ensureAgentLabels,
      conversationId,
      conversationTitle,
      hasEarlier,
      loadingEarlier,
      loadEarlierMessages,
      recentConversations,
      refreshRecent,
      resumingId,
      resumeError,
      resumeConversation,
      removeConversation,
      ensureResumed,
    }),
    [
      turns,
      agentLabels,
      entitySuggestions,
      chipIntroOnboarded,
      activeProfitDrilldown,
      markTurnRevealed,
      ensureAgentLabels,
      conversationId,
      conversationTitle,
      hasEarlier,
      loadingEarlier,
      loadEarlierMessages,
      recentConversations,
      refreshRecent,
      resumingId,
      resumeError,
      resumeConversation,
      removeConversation,
      ensureResumed,
    ],
  );

  return (
    <ZoeControlsContext.Provider value={controls}>
      <ZoeThreadContext.Provider value={thread}>{children}</ZoeThreadContext.Provider>
    </ZoeControlsContext.Provider>
  );
}

/** Controls only: for components that open the pop-up or ask, without re-rendering while ZOE streams. */
export function useZoeControls(): ZoeControlsValue {
  const context = useContext(ZoeControlsContext);
  if (context === undefined) {
    throw new Error('useZoeControls must be used within a ZoeConversationProvider');
  }
  return context;
}

/** For components also rendered outside the app shell (tests, previews): undefined there instead of throwing. */
export function useOptionalZoeControls(): ZoeControlsValue | undefined {
  return useContext(ZoeControlsContext);
}

/** Controls and the thread: for surfaces that render the conversation. */
export function useZoeConversation(): ZoeConversationValue {
  const controls = useContext(ZoeControlsContext);
  const thread = useContext(ZoeThreadContext);
  if (controls === undefined || thread === undefined) {
    throw new Error('useZoeConversation must be used within a ZoeConversationProvider');
  }
  return { ...controls, ...thread };
}
